import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import WorkshopNavigation from './components/WorkshopNavigation';
import useAtelier5Store from './stores/useAtelier5Store';
import useAtelier1Store from './stores/useAtelier1Store';
import Dashboard from './components/Dashboard';
import './styles/navbar-animation.css';
import './styles/modernDesignSystem.css';
import Context from './components/Atelier 1/Cadre/Context';
import BusinessValues from './components/Atelier 1/VM/BusinessValues';
import DreadedEvents from './components/Atelier 1/ER/DreadedEvents';
import SecurityFramework from './components/Atelier 1/Socle/SecurityFramework';
import SecurityControls from './components/Atelier 1/Socle/SecurityControls';
import RiskTreatment from './components/Atelier 1/Socle/RiskTreatment';

import KeyboardShortcutHelp from './components/KeyboardShortcutHelp';
import Activite1 from './components/Atelier 2/Activite1/Activite1';
import Activite2 from './components/Atelier 2/Activite2/Activite2';
import Activite3 from './components/Atelier 2/Activite3/Activite3';
import Activite1Atelier3 from './components/Atelier3/Activite1/Activite1';
import Activite2Atelier3 from './components/Atelier3/Activite2/Activite2';
import Activite3Atelier3 from './components/Atelier3/Activite3/Activite3';
import Activite4Atelier3 from './components/Atelier3/Activite4/Activite4';
import Activite5Atelier3 from './components/Atelier3/Activite5/Activite5.jsx';
import ThreatIntelligence from './components/Atelier4/ThreatIntelligence';
import OperationalScenariosAI from './components/Atelier4/OperationalScenariosAI';

import Atelier5 from './components/Atelier5';
import ImplementationChecklist from './components/Atelier5/ImplementationChecklist/ImplementationChecklist';
import FusionPTRPage from './components/FusionPTR/FusionPTRPage';

import AutoSaveNotification from './components/common/AutoSaveNotification';
import AnalysisSelector from './components/AnalysisSelector';
import CGUModal from './components/common/CGUModal';
import SensitiveDataGuideModal from './components/common/SensitiveDataGuideModal';

import VersionHistoryPanel from './components/common/VersionHistoryPanel';
import VersionSnapshotModal from './components/common/VersionSnapshotModal';
import PresenceIndicator from './components/common/PresenceIndicator';
import NotificationCenter from './components/layout/NotificationCenter';
import ChatWidget from './components/chatbot/ChatWidget';

import { RefreshCw, Menu, X, History, FileText } from 'lucide-react';
import { Toaster } from 'react-hot-toast';

import { ReadOnlyBanner, useReadOnly } from './context/ReadOnlyContext';

import {
  getPreviousTab,
  getNextTab,
  isFirstTab,
  isLastTab,
  getActiveWorkshop,
  getCompletedTabs,
  markTabAsCompleted
} from './utils/navigationUtils';

import {
  getCurrentAnalysisId
} from './utils/appStateManagerAPI';

import useAnalysisStore from './stores/useAnalysisStore';

import { requestSilentSave } from './utils/silentSaveDispatch';

import { userService } from './services/apiServices';

const NoAnalysisFallback = () => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center min-h-[400px] px-8 py-8">
      <div className="w-full max-w-6xl space-y-6">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg mb-4">
            <FileText size={28} className="text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800">
            {t('noAnalysis.title')}
          </h2>
          <p className="text-gray-500 mt-1 max-w-md mx-auto">
            {t('noAnalysis.message')}
          </p>
        </div>
        <AnalysisSelector />
      </div>
    </div>
  );
};

// Note: user et handleLogout seront injectés par AuthWrapper
const EbiosRMApp = ({ user, handleLogout }) => {
  const { i18n, t } = useTranslation(); // Add i18n hook for language switching
  const navigate = useNavigate();
  const { isReadOnly } = useReadOnly(); // Check if viewer mode is active

  // Initialize activeTab from localStorage or default to 'dashboard'
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('ebiosrm_active_tab');
    return savedTab || 'dashboard';
  });
  const [expandedWorkshops, setExpandedWorkshops] = useState({ 'workshop1': false, 'workshop2': false, 'workshop3': false, 'workshop4': false, 'workshop5': false });
  const [isLoading, setIsLoading] = useState(false);
  const [isSidebarOpen, setSidebarOpen] = useState(true); // State for sidebar visibility
  const [isLanguageTransitioning, setIsLanguageTransitioning] = useState(false); // For language change animation
  const [completedTabs, setCompletedTabs] = useState([]); // Progression tracking

  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [showCGU, setShowCGU] = useState(false);
  const [showSensitiveDataGuide, setShowSensitiveDataGuide] = useState(false);
  const [liveCompanyName, setLiveCompanyName] = useState(user?.companyName || '');

  // Fetch live company name for the navbar
  useEffect(() => {
    const fetchLiveCompanyName = async () => {
      try {
        const { dashboardService } = await import('./services/dashboardServices');
        const response = await dashboardService.getProviderMetrics();
        if (response.success && response.data?.companyInfo?.name) {
          setLiveCompanyName(response.data.companyInfo.name);
        }
      } catch (error) {
        console.warn('Could not fetch live company name for navbar');
        setLiveCompanyName(user?.companyName || '');
      }
    };

    if (user) {
      fetchLiveCompanyName();
    }
  }, [user]);

  // Auto-show CGU on first connection
  useEffect(() => {
    const cguAccepted = localStorage.getItem('ebios_cgu_accepted');
    if (user && !cguAccepted) {
      setShowCGU(true);
    }
  }, [user]);

  const handleCloseCGU = async () => {
    localStorage.setItem('ebios_cgu_accepted', 'true');
    setShowCGU(false);

    if (user) {
      try {
        await userService.acceptCgu();
      } catch (error) {
        console.error('Failed to save CGU acceptance to database:', error);
      }
    }
  };

  // Version history state (global, accessible from navbar)
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [snapshotVersion, setSnapshotVersion] = useState(null);


  // Listen for language changes and trigger smooth animation
  useEffect(() => {
    const handleLanguageChange = () => {
      setIsLanguageTransitioning(true);
      // Remove the class after animation completes
      setTimeout(() => {
        setIsLanguageTransitioning(false);
      }, 350); // Match the CSS animation duration
    };

    // Subscribe to language change events
    i18n.on('languageChanged', handleLanguageChange);

    // Cleanup
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);


  // Set sidebar state based on screen size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) { // 768px is the md breakpoint in Tailwind
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    // Set initial state
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Listen for tab switching events from Dashboard component
  useEffect(() => {
    const handleTabSwitch = () => {
      const tabName = localStorage.getItem('ebiosrm_switch_to_tab');
      if (tabName) {
        setActiveTab(tabName);
        localStorage.removeItem('ebiosrm_switch_to_tab'); // Clean up
      }
    };

    // Add event listener
    window.addEventListener('ebiosrm_switch_tab', handleTabSwitch);

    // Cleanup
    return () => window.removeEventListener('ebiosrm_switch_tab', handleTabSwitch);
  }, []);

  const analysisStore = useAnalysisStore();
  const currentAnalysis = analysisStore.currentAnalysis;

  // A1 data now managed by useAtelier1Store — no local state needed

  // Load data when component mounts — data loading handled by AnalysisContext + stores
  useEffect(() => {
    // Initial load of completed tabs
    setCompletedTabs(getCompletedTabs(getCurrentAnalysisId()));

    // Listen for when the analysis ID becomes known (e.g. after async login restore).
    // This ensures the sidebar progress circles are populated on first login
    // without needing a page refresh.
    const handleAnalysisLoaded = (e) => {
      const analysisId = e.detail?.analysisId || getCurrentAnalysisId();
      if (analysisId) {
        setCompletedTabs(getCompletedTabs(analysisId));
      }
    };

    // Listen for progression updates (individual tab marked completed)
    const handleProgressionUpdate = (e) => {
      if (e.detail && e.detail.completedTabs) {
        setCompletedTabs([...e.detail.completedTabs]);
      }
    };

    // Listen for manual saves to mark the current tab as completed
    const handleManualSave = () => {
      const currentTab = localStorage.getItem('ebiosrm_active_tab');
      const analysisId = localStorage.getItem('currentAnalysisId');
      if (currentTab && currentTab !== 'dashboard' && analysisId) {
        console.log(`[EbiosRMApp] Manual save detected, marking ${currentTab} as completed`);
        markTabAsCompleted(currentTab, analysisId);
      }
    };

    window.addEventListener('ebiosrm_analysis_loaded', handleAnalysisLoaded);
    window.addEventListener('ebiosrm_progression_updated', handleProgressionUpdate);
    window.addEventListener('ebiosrm_manual_save', handleManualSave);

    return () => {
      window.removeEventListener('ebiosrm_analysis_loaded', handleAnalysisLoaded);
      window.removeEventListener('ebiosrm_progression_updated', handleProgressionUpdate);
      window.removeEventListener('ebiosrm_manual_save', handleManualSave);
    };
  }, []);

  // Persist activeTab to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('ebiosrm_active_tab', activeTab);
  }, [activeTab]);


  // Data loading handled by AnalysisContext + stores — no local loading needed

  // loadAllData removed — A1 data loaded by useAtelier1Store, other data by AnalysisContext

  // saveData removed — A1 data saved by useAtelier1Store

  // Function to handle tab change with auto-save
  // Only shows the save notification when data is actually being saved
  const handleTabChange = async (newTab) => {
    let saveResult = 'skipped';
    try {
      if (activeTab !== 'dashboard' && activeTab !== 'atelier5-fusion-ptr') {
        if (activeTab === 'atelier5-synthese-scenarios' || activeTab === 'atelier5-implementation-checklist') {
          saveResult = await useAtelier5Store.getState().silentSave();
        } else {
          saveResult = await requestSilentSave(activeTab);
        }
      }
      if (saveResult === true) {
        setIsAutoSaving(true);
        await new Promise(resolve => setTimeout(resolve, 800));
        setIsAutoSaving(false);
        markTabAsCompleted(activeTab, getCurrentAnalysisId());
      }
    } catch (error) {
      console.error('Auto-save error:', error);
      setIsAutoSaving(false);
    }
    localStorage.setItem('ebiosrm_active_tab', newTab);
    setActiveTab(newTab);
  };

  // Function to navigate to the previous tab
  const handlePrevious = () => {
    if (activeTab === 'dashboard') {
      // If on dashboard, do nothing as it's the first screen
      return;
    }

    if (!isFirstTab(activeTab)) {
      const currentWorkshop = getActiveWorkshop(activeTab);
      const { nextTab } = getPreviousTab(activeTab, currentWorkshop);
      handleTabChange(nextTab);
    }
  };

  // Function to navigate to the next tab
  const handleNext = () => {
    if (activeTab === 'dashboard') {
      // If on dashboard, go to the first tab of the normal flow
      handleTabChange('context');
      return;
    }

    if (!isLastTab(activeTab)) {
      const currentWorkshop = getActiveWorkshop(activeTab);
      const { nextTab } = getNextTab(activeTab, currentWorkshop);
      handleTabChange(nextTab);
    }
  };

  // Handle workshop tree structure
  const toggleWorkshop = (workshop) => {
    setExpandedWorkshops({
      ...expandedWorkshops,
      [workshop]: !expandedWorkshops[workshop]
    });
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    // Function to handle keyboard shortcuts
    const handleKeyDown = (event) => {
      // Check for Ctrl + Right Arrow (next tab)
      if (event.ctrlKey && event.key === 'ArrowRight') {
        event.preventDefault(); // Prevent default browser behavior
        if (!isLastTab(activeTab)) {
          handleNext();
        }
      }

      // Check for Ctrl + Left Arrow (previous tab)
      if (event.ctrlKey && event.key === 'ArrowLeft') {
        event.preventDefault(); // Prevent default browser behavior
        if (activeTab !== 'dashboard' && !isFirstTab(activeTab)) {
          handlePrevious();
        }
      }
    };

    // Add event listener
    window.addEventListener('keydown', handleKeyDown);

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeTab]); // Re-establish the event listener when activeTab changes

  // Helper function to check if the current tab is a workshop tab (including Workshop 1)
  const isWorkshopTab = (tab) => {
    const workshopTabs = [
      // Workshop 1 tabs
      'context', 'business-values', 'events', 'security', 'security-controls', 'risk-treatment',
      // Workshop 2 tabs
      'atelier2-activite1', 'atelier2-activite2', 'atelier2-activite3',
      // Workshop 3 tabs
      'atelier3-activite1', 'atelier3-activite2', 'atelier3-activite3', 'atelier3-activite4', 'atelier3-activite5',
      // Workshop 4 tabs
      'atelier4-threat-intelligence', 'atelier4-cti-scenarios',
      // Workshop 5 tabs
      'atelier5-synthese-scenarios', 'atelier5-strategie-traitement', 'atelier5-mesures-securite',
      'atelier5-evaluation-residuels', 'atelier5-plan-traitement', 'atelier5-cadre-suivi',
      'atelier5-implementation-checklist',
      // Fusionnement des PTR - standalone section after Workshop 5
      'atelier5-fusion-ptr'
    ];
    return workshopTabs.includes(tab);
  };

  return (
    <div className={`flex flex-col min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 ${isLanguageTransitioning ? 'language-transition' : ''}`}>
      {/* Auto-save notification - minimal circular design */}
      <AutoSaveNotification isVisible={isAutoSaving} />

      {/* Modern Toast Container */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'rgba(255, 255, 255, 0.95)',
            color: '#374151',
            borderRadius: '12px',
            padding: '16px',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          },
          success: {
            style: {
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              color: '#fff',
            },
            iconTheme: {
              primary: '#ffffff',
              secondary: '#10B981',
            },
          },
          error: {
            style: {
              background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
              color: '#fff',
            },
            iconTheme: {
              primary: '#ffffff',
              secondary: '#EF4444',
            },
          },
        }}
      />
      {/* Sidebar with Tree Structure - Positioned absolutely */}
      <WorkshopNavigation
        activeTab={activeTab}
        setActiveTab={handleTabChange}
        expandedWorkshops={expandedWorkshops}
        toggleWorkshop={toggleWorkshop}
        isSidebarOpen={isSidebarOpen}
        completedTabs={completedTabs}
      />

      {/* Overlay for mobile when sidebar is open */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-10 md:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        ></div>
      )}

      {/* Sidebar Toggle Button - Mobile */}
      <button
        onClick={() => setSidebarOpen(!isSidebarOpen)}
        className="fixed left-0 top-20 z-30 bg-blue-600 text-white p-2 rounded-r-lg shadow-md hover:bg-blue-700 transition-all duration-300"
        aria-label={isSidebarOpen ? "Fermer le menu" : "Ouvrir le menu"}
      >
        {isSidebarOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Content that shifts with sidebar */}
      <div
        className={`flex-grow flex flex-col transition-all duration-500 ease-out ${isSidebarOpen ? 'ml-72' : 'ml-0'}`}
        style={{ maxWidth: isSidebarOpen ? 'calc(100% - 18rem)' : '100%' }}
      >
        {/* Main header with Modern & Vibrant Design */}
        {/* Main header with Professional & High-Contrast Design */}
        <header className="relative bg-[#0F172A] shadow-2xl z-20 border-b border-white/5">
          <div className="relative z-10 px-8 py-5 flex items-center justify-center">
            {/* Center Title - Clean Typography & High Contrast */}
            <div className="flex flex-col items-center justify-center">
              <h1 className="text-4xl font-extrabold tracking-tight flex items-center">
                <span className="text-white drop-shadow-sm">EBIOS</span>
                <span className="text-blue-500 ml-0">ENS</span>
              </h1>

              <div className="flex items-center gap-4 mt-2">
                <div className="h-[1px] w-12 bg-blue-500/40"></div>
                <span className="text-[11px] font-bold uppercase tracking-[0.4em] text-blue-200/80 font-mono">
                  Risk Management Platform
                </span>
                <div className="h-[1px] w-12 bg-blue-500/40"></div>
              </div>
            </div>
          </div>

          {/* Subtle Indirect French Flag Accent at Bottom */}
          <div className="absolute bottom-0 left-0 w-full h-[4px] flex">
            <div className="h-full flex-1 bg-[#0055A4] opacity-40"></div>
            <div className="h-full flex-1 bg-white opacity-40"></div>
            <div className="h-full flex-1 bg-[#EF4135] opacity-40"></div>
          </div>
        </header>

        {/* Secondary Navigation Bar - User Info & Controls */}
        {user && (
          <nav className="bg-slate-800/95 backdrop-blur-sm text-white px-5 py-1 flex justify-between items-center shadow-md border-b border-slate-700/50">
            <div className="flex items-center space-x-3">
              {/* User Avatar & Name */}
              <div className="flex items-center bg-slate-700/60 px-2 py-1 rounded-md border border-slate-600/50 hover:bg-slate-700 transition-colors">
                <div className="w-5 h-5 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center mr-1.5 shadow-sm">
                  <span className="text-white font-bold text-[10px]">{user.name?.charAt(0).toUpperCase() || 'U'}</span>
                </div>
                <span className="text-xs font-medium text-slate-100">{user.name}</span>
              </div>

              {/* Company Name */}
              {liveCompanyName && (
                <div className="flex items-center bg-orange-500/20 px-2 py-1 rounded-md border border-orange-500/30">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3 mr-1 text-orange-400">
                    <path fillRule="evenodd" d="M4.5 2.25a.75.75 0 000 1.5v16.5h-.75a.75.75 0 000 1.5h16.5a.75.75 0 000-1.5h-.75V3.75a.75.75 0 000-1.5h-15zM9 6a.75.75 0 000 1.5h1.5a.75.75 0 000-1.5H9zm-.75 3.75A.75.75 0 019 9h1.5a.75.75 0 010 1.5H9a.75.75 0 01-.75-.75zM9 12a.75.75 0 000 1.5h1.5a.75.75 0 000-1.5H9zm3.75-5.25A.75.75 0 0113.5 6H15a.75.75 0 010 1.5h-1.5a.75.75 0 01-.75-.75zM13.5 9a.75.75 0 000 1.5H15A.75.75 0 0015 9h-1.5zm-.75 3.75a.75.75 0 01.75-.75H15a.75.75 0 010 1.5h-1.5a.75.75 0 01-.75-.75z" clipRule="evenodd" />
                  </svg>
                  <span className="text-xs font-semibold text-orange-300">{liveCompanyName}</span>
                </div>
              )}

              {/* Current Analysis */}
              {currentAnalysis && (
                <>
                  <div className="flex items-center gap-2 bg-blue-500/20 px-3 py-1.5 rounded-lg border border-blue-500/30">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-blue-400">
                      <path d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0016.5 9h-1.875a1.875 1.875 0 01-1.875-1.875V5.25A3.75 3.75 0 009 1.5H5.625z" />
                      <path d="M12.971 1.816A5.23 5.23 0 0114.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 013.434 1.279 9.768 9.768 0 00-6.963-6.963z" />
                    </svg>
                    <span className="text-sm text-blue-300">{currentAnalysis.name}</span>
                    {/* Version Badge */}
                    <span className="text-[10px] font-mono px-1.5 py-0.5 bg-blue-400/20 text-blue-300 rounded border border-blue-400/30">
                      v{currentAnalysis.version || '1.0'}
                    </span>
                  </div>
                  {/* History Button */}
                  <button
                    onClick={() => setShowVersionHistory(true)}
                    className="flex items-center px-2 py-1 text-xs bg-slate-700/60 hover:bg-slate-600 text-slate-300 hover:text-white rounded-md border border-slate-600/50 transition-all duration-200"
                    title={t('versionManagement.history.buttonTooltip', "Voir l'historique des versions")}
                  >
                    <History size={14} className="mr-1" />
                    <span className="hidden lg:inline">{t('versionManagement.history.title', 'Historique')}</span>
                  </button>
                </>
              )}
            </div>

            <div className="flex items-center gap-3">
              {/* Notification Center */}
              <NotificationCenter />

              {/* Admin Dashboard Link - Only for admins */}
              {user && (user.role === 'admin' || user.role === 'superadmin') && (
                <button
                  onClick={() => navigate('/admin/dashboard')}
                  className="flex items-center px-3 py-1.5 text-xs bg-slate-700 hover:bg-slate-600 text-white rounded-md border border-slate-600 shadow-sm transition-all duration-200 font-medium"
                  title={t('common.administration', 'Administration')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 mr-2 text-blue-400">
                    <path fillRule="evenodd" d="M11.078 2.25c-.917 0-1.699.663-1.85 1.567L9.05 4.889c-.02.12-.115.26-.297.348a7.493 7.493 0 00-.986.57c-.166.115-.334.126-.45.083L6.3 5.508a1.875 1.875 0 00-2.282.819l-.922 1.597a1.875 1.875 0 00.432 2.385l.84.692c.095.078.17.229.154.43a7.598 7.598 0 000 1.139c.015.2-.059.352-.153.43l-.841.692a1.875 1.875 0 00-.432 2.385l.922 1.597a1.875 1.875 0 002.282.818l1.019-.382c.115-.043.283-.031.45.082.312.214.641.405.985.57.182.088.277.228.297.35l.178 1.071c.151.904.933 1.567 1.85 1.567h1.844c.916 0 1.699-.663 1.85-1.567l.178-1.072c.02-.12.114-.26.297-.349.344-.165.673-.356.985-.57.167-.114.335-.125.45-.082l1.02.382a1.875 1.875 0 002.28-.819l.922-1.597a1.875 1.875 0 00-.432-2.385l-.84-.692c-.095-.078-.17-.229-.154-.43a7.614 7.614 0 000-1.139c-.016-.2.059-.352.153-.43l.84-.692c.708-.582.891-1.59.433-2.385l-.922-1.597a1.875 1.875 0 00-2.282-.818l-1.02.382c-.114.043-.282.031-.449-.083a7.49 7.49 0 00-.985-.57c-.183-.087-.277-.227-.297-.348l-.179-1.072a1.875 1.875 0 00-1.85-1.567h-1.843zM12 15.75a3.75 3.75 0 100-7.5 3.75 3.75 0 000 7.5z" clipRule="evenodd" />
                  </svg>
                  {t('common.administration', 'Administration')}
                </button>
              )}

              {/* Language Toggle - Enhanced */}
              <div className="flex items-center bg-slate-700/50 rounded-lg p-0.5 border border-slate-600/50">
                <button
                  onClick={() => i18n.changeLanguage('fr')}
                  className={`flex items-center px-2 py-1 text-xs rounded-md transition-all duration-200 ${i18n.language === 'fr'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-slate-300 hover:text-white hover:bg-slate-600'
                    }`}
                  title="Français"
                >
                  <span className="fi fi-fr mr-1.5" style={{ fontSize: '1rem' }}></span>
                  <span className="font-semibold text-xs">FR</span>
                </button>
                <button
                  onClick={() => i18n.changeLanguage('en')}
                  className={`flex items-center px-2 py-1 text-xs rounded-md transition-all duration-200 ${i18n.language === 'en'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-slate-300 hover:text-white hover:bg-slate-600'
                    }`}
                  title="English"
                >
                  <span className="fi fi-gb mr-1.5" style={{ fontSize: '1rem' }}></span>
                  <span className="font-semibold text-xs">EN</span>
                </button>
              </div>

              {/* Logout Button - Enhanced */}
              <button
                onClick={handleLogout}
                className="flex items-center px-3 py-1.5 text-xs bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-md shadow-sm hover:shadow-md transition-all duration-200 font-medium"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 mr-2">
                  <path fillRule="evenodd" d="M7.5 3.75A1.5 1.5 0 006 5.25v13.5a1.5 1.5 0 001.5 1.5h6a1.5 1.5 0 001.5-1.5V15a.75.75 0 011.5 0v3.75a3 3 0 01-3 3h-6a3 3 0 01-3-3V5.25a3 3 0 013-3h6a3 3 0 013 3V9a.75.75 0 01-1.5 0V5.25a1.5 1.5 0 00-1.5-1.5h-6zm10.72 4.72a.75.75 0 011.06 0l3 3a.75.75 0 010 1.06l-3 3a.75.75 0 11-1.06-1.06l1.72-1.72H9a.75.75 0 010-1.5h10.94l-1.72-1.72a.75.75 0 010-1.06z" clipRule="evenodd" />
                </svg>
                {t('common.logout')}
              </button>
            </div>
          </nav>
        )}

        {/* Read-Only Mode Banner - Shown for viewer accounts */}
        <ReadOnlyBanner />

        {/* Loading indicator */}
        {isLoading && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
            <div className="bg-white p-4 rounded-lg shadow-lg flex items-center">
              <RefreshCw size={24} className="text-blue-500 mr-3 animate-spin" />
              <span className="text-gray-800">{t('common.loading')}</span>
            </div>
          </div>
        )}

        {/* Modern Main Content */}
        <div className="flex-grow p-6">
          <div className="modern-card modern-fade-in">
            {/* Dashboard Tab */}
            {activeTab === 'dashboard' && (
              <Dashboard />
            )}



            {/* Context Tab */}
            {activeTab === 'context' && (
              getCurrentAnalysisId() ? (
                <Context />
              ) : (
                <NoAnalysisFallback />
              )
            )}

            {/* Business Values Tab */}
            {activeTab === 'business-values' && (
              getCurrentAnalysisId() ? (
                <BusinessValues />
              ) : (
                <NoAnalysisFallback />
              )
            )}

            {/* Events Tab */}
            {activeTab === 'events' && (
              getCurrentAnalysisId() ? (
                <DreadedEvents />
              ) : (
                <NoAnalysisFallback />
              )
            )}

            {/* Security Framework Tab */}
            {activeTab === 'security' && (
              getCurrentAnalysisId() ? (
                <SecurityFramework key={i18n.language} />
              ) : (
                <NoAnalysisFallback />
              )
            )}

            {/* Security Controls Tab */}
            {activeTab === 'security-controls' && (
              getCurrentAnalysisId() ? (
                <SecurityControls />
              ) : (
                <NoAnalysisFallback />
              )
            )}

            {/* Risk Treatment Tab */}
            {activeTab === 'risk-treatment' && (
              getCurrentAnalysisId() ? (
                <RiskTreatment />
              ) : (
                <NoAnalysisFallback />
              )
            )}

            {/* Atelier 2 - Activité 1 */}
            {activeTab === 'atelier2-activite1' && (
              getCurrentAnalysisId() ? (
                <Activite1 />
              ) : (
                <NoAnalysisFallback />
              )
            )}

            {/* Atelier 2 - Activité 2 */}
            {activeTab === 'atelier2-activite2' && (
              getCurrentAnalysisId() ? (
                <Activite2 />
              ) : (
                <NoAnalysisFallback />
              )
            )}

            {/* Atelier 2 - Activité 3 */}
            {activeTab === 'atelier2-activite3' && (
              getCurrentAnalysisId() ? (
                <Activite3 />
              ) : (
                <NoAnalysisFallback />
              )
            )}
            {/* Atelier 3 - Activité 1 */}
            {activeTab === 'atelier3-activite1' && (
              getCurrentAnalysisId() ? (
                <Activite1Atelier3 />
              ) : (
                <NoAnalysisFallback />
              )
            )}
            {/* Atelier 3 - Activité 2 */}
            {activeTab === 'atelier3-activite2' && (
              getCurrentAnalysisId() ? (
                <Activite2Atelier3 />
              ) : (
                <NoAnalysisFallback />
              )
            )}
            {/* Atelier 3 - Activité 3 */}
            {activeTab === 'atelier3-activite3' && (
              getCurrentAnalysisId() ? (
                <Activite3Atelier3 />
              ) : (
                <NoAnalysisFallback />
              )
            )}
            {/* Atelier 3 - Activité 4 */}
            {activeTab === 'atelier3-activite4' && (
              getCurrentAnalysisId() ? (
                <Activite4Atelier3 />
              ) : (
                <NoAnalysisFallback />
              )
            )}
            {/* Atelier 3 - Activité 5 */}
            {activeTab === 'atelier3-activite5' && (
              getCurrentAnalysisId() ? (
                <Activite5Atelier3 />
              ) : (
                <NoAnalysisFallback />
              )
            )}
            {/* Atelier 4 - Phase 1: Threat Intelligence */}
            {activeTab === 'atelier4-threat-intelligence' && (
              getCurrentAnalysisId() ? (
                <ThreatIntelligence />
              ) : (
                <NoAnalysisFallback />
              )
            )}

            {/* Atelier 4 - Phase 2: CTI-based Operational Scenarios AI */}
            {activeTab === 'atelier4-cti-scenarios' && (
              getCurrentAnalysisId() ? (
                <OperationalScenariosAI />
              ) : (
                <NoAnalysisFallback />
              )
            )}



            {/* Atelier 5 - All Activities */}
            {(activeTab === 'atelier5-synthese-scenarios' ||
              activeTab === 'atelier5-strategie-traitement' ||
              activeTab === 'atelier5-mesures-securite' ||
              activeTab === 'atelier5-evaluation-residuels' ||
              activeTab === 'atelier5-plan-traitement' ||
              activeTab === 'atelier5-cadre-suivi') && (
                getCurrentAnalysisId() ? (
                  <Atelier5 activeTab={activeTab} />
                ) : (
                  <NoAnalysisFallback />
                )
              )}

            {/* Implementation Checklist - Independent Route */}
            {activeTab === 'atelier5-implementation-checklist' && (
              getCurrentAnalysisId() ? (
                <ImplementationChecklist
                  analysisId={getCurrentAnalysisId() || '67f2e2a0a5ad300283807d49'}
                />
              ) : (
                <NoAnalysisFallback />
              )
            )}

            {/* Fusionnement des PTR - Standalone multi-analysis page */}
            {activeTab === 'atelier5-fusion-ptr' && (
              <FusionPTRPage />
            )}


          </div>

          {/* Modern Navigation buttons */}
          <div className="flex justify-between items-center mt-6">
            <button
              className={`modern-btn ${activeTab === 'dashboard' || isFirstTab(activeTab)
                ? 'opacity-50 cursor-not-allowed bg-gray-200 text-gray-500'
                : 'modern-btn-secondary'
                }`}
              onClick={handlePrevious}
              disabled={activeTab === 'dashboard' || isFirstTab(activeTab)}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t('common.previous')}
            </button>



            {!isLastTab(activeTab) && (
              <button
                className="modern-btn modern-btn-primary"
                onClick={handleNext}
              >
                {activeTab === 'dashboard' ? t('common.startAnalysis') : t('common.next')}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Modern Footer */}
        <footer className="modern-backdrop-blur border-t border-white/20 p-4 text-center text-gray-600 mt-auto">
          <div className="flex items-center justify-center space-x-2">
            <div className="w-6 h-6 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xs">E</span>
            </div>
            <span className="font-medium">{t('footer.copyright')}</span>
            <span className="text-gray-400">|</span>
            <button
              onClick={() => setShowCGU(true)}
              className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium transition-colors"
            >
              {t('cgu.footerLink', 'CGU')}
            </button>
            <span className="text-gray-400">|</span>
            <button
              onClick={() => setShowSensitiveDataGuide(true)}
              className="text-sm text-emerald-600 hover:text-emerald-800 hover:underline font-medium transition-colors"
            >
              {t('sensitiveData.footerLink', '🛡️ Données sensibles')}
            </button>
          </div>
        </footer>
        <KeyboardShortcutHelp />
        <CGUModal isOpen={showCGU} onClose={handleCloseCGU} />
        <SensitiveDataGuideModal isOpen={showSensitiveDataGuide} onClose={() => setShowSensitiveDataGuide(false)} />

        {/* Real-time active users indicator */}
        {getCurrentAnalysisId() && activeTab !== 'dashboard' && (
          <PresenceIndicator roomId={`${getCurrentAnalysisId()}-${activeTab}`} />
        )}

        {/* Global Version History Panel (accessible from all pages) */}
        <VersionHistoryPanel
          isOpen={showVersionHistory}
          versions={currentAnalysis?.versionHistory || []}
          onClose={() => setShowVersionHistory(false)}
          onViewSnapshot={(version) => {
            setSnapshotVersion({
              analysisId: currentAnalysis?.id,
              version: version
            });
          }}
          currentVersion={currentAnalysis?.version}
        />

        <VersionSnapshotModal
          isOpen={!!snapshotVersion}
          onClose={() => setSnapshotVersion(null)}
          analysisId={snapshotVersion?.analysisId}
          version={snapshotVersion?.version}
        />
        <ChatWidget />
      </div>
    </div>
  );
};

export default EbiosRMApp;
